import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { journalAPI } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, MagnifyingGlass, Notebook } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function JournalEntriesPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [expandedEntry, setExpandedEntry] = useState(null);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const response = await journalAPI.list({ limit: 100 });
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const filteredEntries = entries.filter(entry => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        entry.entry_number?.toLowerCase().includes(searchLower) ||
        entry.description?.toLowerCase().includes(searchLower) ||
        entry.reference?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getSourceBadgeColor = (source) => {
    switch(source) {
      case 'zoho': return 'bg-blue-100 text-blue-700';
      case 'bank': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div data-testid="journal-entries-page">
      <PageHeader 
        title="Journal Entries" 
        subtitle={`${total} total entries`}
        actions={
          <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="add-journal-btn">
            <Plus size={16} className="mr-2" />
            New Entry
          </Button>
        }
      />
      
      <div className="page-content">
        {/* Search */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              placeholder="Search entries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-journal"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Entry #</th>
                <th>Date</th>
                <th>Description</th>
                <th>Reference</th>
                <th>Source</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j}><div className="skeleton h-4 w-20 rounded"></div></td>
                    ))}
                  </tr>
                ))
              ) : filteredEntries.length > 0 ? (
                filteredEntries.map((entry) => (
                  <>
                    <tr 
                      key={entry.id} 
                      className="cursor-pointer"
                      onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                      data-testid={`journal-row-${entry.entry_number}`}
                    >
                      <td className="font-medium font-mono text-sm">{entry.entry_number}</td>
                      <td>{formatDate(entry.date)}</td>
                      <td className="max-w-xs truncate">{entry.description}</td>
                      <td className="text-[#64748B]">{entry.reference || '-'}</td>
                      <td>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${getSourceBadgeColor(entry.source)}`}>
                          {entry.source}
                        </span>
                      </td>
                      <td className="font-medium">{formatCurrency(entry.total_amount)}</td>
                      <td>
                        <span className="status-pill paid">{entry.status}</span>
                      </td>
                    </tr>
                    {expandedEntry === entry.id && (
                      <tr>
                        <td colSpan={7} className="bg-[#F8FAFC] p-4">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-3">Journal Lines</h4>
                          <table className="w-full">
                            <thead>
                              <tr className="text-xs text-[#64748B]">
                                <th className="text-left py-2">Account</th>
                                <th className="text-left py-2">Description</th>
                                <th className="text-right py-2">Debit</th>
                                <th className="text-right py-2">Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.entries?.map((line, i) => (
                                <tr key={i} className="border-t border-[#E2E8F0]">
                                  <td className="py-2 font-mono text-sm">{line.account_code}</td>
                                  <td className="py-2 text-sm">{line.description}</td>
                                  <td className="py-2 text-right">
                                    {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                                  </td>
                                  <td className="py-2 text-right">
                                    {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[#64748B]">
                    <Notebook size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No journal entries found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
